import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

interface UnblockAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (resolutionNote: string) => Promise<void>;
  isPending: boolean;
}

const schema = z.object({
  resolutionNote: z
    .string()
    .trim()
    .min(1, "A resolution note is required to change the status off of blocked"),
});

type FormValues = z.infer<typeof schema>;

export function UnblockAuditDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: UnblockAuditDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { resolutionNote: "" },
  });

  useEffect(() => {
    if (open) form.reset({ resolutionNote: "" });
  }, [open, form]);

  async function onSubmit(values: FormValues) {
    await onConfirm(values.resolutionNote);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Resolve Blocked Status</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="resolutionNote"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Resolution Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Explain how this audit was unblocked…"
                      rows={4}
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving…" : "Continue"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
